import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Grid,
    Typography,
    OutlinedInput,
    InputAdornment,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { IconSearch } from '@tabler/icons';
import * as XLSX from 'xlsx';

import { useHistory } from 'react-router-dom';
import { token } from '../../../../../api';
import api from '../../../../services/api';

import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

const IMPORT_MAX_ROWS = 500;

// aceita cabeçalhos com/sem acento e maiúscula/minúscula (ex: "Nome", "nome", "NOME")
const HEADER_TO_FIELD = {
    nome: 'name',
    name: 'name',
    cnpj: 'cnpj',
    'cnpj/cpf': 'cnpj',
    email: 'email',
    'e-mail': 'email',
    endereco: 'address',
    address: 'address',
    descricao: 'description',
    description: 'description'
};

const normalizeHeader = (header) =>
    String(header || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // remove acentos
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ') // colapsa espaços duplos ("CNPJ  " -> "cnpj")
        .replace(/\s*\/\s*/g, '/'); // "CPF / CNPJ" e "CPF/ CNPJ" -> "cpf/cnpj"

// yyyy-mm-dd a partir das partes locais do Date (evita o typo clássico de
// usar toISOString(), que converte pra UTC e pode voltar um dia pro
// passado dependendo do fuso)
const formatDateCellValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// mesma lógica de `formatDateCellValue`, só que pra célula formatada como
// hora no Excel (o XLSX ainda devolve um Date - só a parte de hora importa)
const formatTimeCellValue = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

// linhas de planilha que casam com o título de um campo customizado vão pra
// dentro de `fields` (mesmo formato que o backend espera nos outros
// endpoints), separado dos campos fixos da Organização
const parseSpreadsheetRows = (workbook, fieldsConfig = []) => {
    const firstSheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[firstSheetName];
    const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });

    const customFieldByHeader = {};
    fieldsConfig.forEach((f) => {
        customFieldByHeader[normalizeHeader(f.title)] = f;
    });

    return rawRows.map((rawRow) => {
        const mapped = {};
        const fields = {};
        Object.entries(rawRow).forEach(([header, value]) => {
            const normalized = normalizeHeader(header);
            const field = HEADER_TO_FIELD[normalized];
            if (field) {
                mapped[field] = String(value ?? '').trim();
                return;
            }
            const customField = customFieldByHeader[normalized];
            if (customField) {
                if (customField.type_field === 'date' && value instanceof Date) {
                    fields[customField.name] = formatDateCellValue(value);
                } else if (customField.type_field === 'time' && value instanceof Date) {
                    fields[customField.name] = formatTimeCellValue(value);
                } else {
                    fields[customField.name] = String(value ?? '').trim();
                }
            }
        });
        if (Object.keys(fields).length > 0) {
            mapped.fields = fields;
        }
        return mapped;
    });
};

const downloadOrganizationTemplate = (fieldsConfig = []) => {
    const headers = ['Nome', 'CNPJ', 'Email', 'Endereço', 'Descrição', ...fieldsConfig.map((f) => f.title)];
    const sheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Organizações');
    XLSX.writeFile(workbook, 'modelo_importacao_organizacoes.xlsx');
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1
});

const useStyles = makeStyles((theme) => ({
    searchControl: {
        width: '434px',
        marginLeft: '16px',
        marginBottom: '8px',
        background: '#fff',
        [theme.breakpoints.down('lg')]: { width: '100%' }
    },
    popperResults: {
        position: 'absolute',
        zIndex: 1200,
        width: '434px',
        marginLeft: '16px',
        backgroundColor: '#fff',
        boxShadow: theme.shadows[3],
        maxHeight: '250px',
        overflowY: 'auto',
        borderRadius: '8px',
        [theme.breakpoints.down('lg')]: { width: '100%' }
    },
    listItemHover: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            cursor: 'pointer'
        }
    }
}));

const ViewOrganization = () => {
    const [appId, setAppId] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [selectedOrganizations, setSelectedOrganizations] = useState([]);

    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importParseError, setImportParseError] = useState('');
    const [fieldsConfig, setFieldsConfig] = useState([]);

    const classes = useStyles();
    const history = useHistory();

    // 🔹 Buscar app padrão
    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            const data = response.data;

            if (data?.app?.id) {
                setAppId(data.app.id);
            } else {
                setStatus({ type: 'error', message: 'App padrão não encontrado' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
        }
    };

    const loadFields = async (currentAppId) => {
        try {
            const response = await api.get(`v1/apps/${currentAppId}/organization_fields/`);
            setFieldsConfig(response.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    // 🔹 INIT
    useEffect(() => {
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadFields(appId);
    }, [appId]);

    // 🔹 Listar organizações quando tiver appId
    useEffect(() => {
        if (!appId) return;
        listarOrganizacoes();
    }, [appId]);

    const listarOrganizacoes = async () => {
        if (!appId) return;

        try {
            const response = await api.get(`v1/apps/${appId}/organizations/`);

            if (response.data) {
                setOrganizations(response.data.organizations);
            } else {
                setStatus({ type: 'error', message: 'Não foi possível carregar as organizações' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar organizações' });
        }
    };

    // 🔥 debounce CORRETO (com appId)
    const handleSearch = useMemo(() => {
        return debounce(async (value, currentAppId) => {
            if (!value || !currentAppId) {
                setSearchResults([]);
                setShowResults(false);
                return;
            }

            try {
                const response = await api.get(`v1/apps/${currentAppId}/organizations/?search=${encodeURIComponent(value)}`);

                if (response.data) {
                    setSearchResults(response.data.organizations);
                    setShowResults(true);
                } else {
                    setStatus({ type: 'error', message: 'Não foi possível carregar os dados' });
                }
            } catch (error) {
                console.error('Erro na busca:', error);
                setStatus({ type: 'error', message: 'Erro na busca' });
            }
        }, 400);
    }, []);

    // 🔹 Input change
    const handleChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (!appId) return;

        handleSearch(value, appId); // passa appId correto
    };

    // 🔹 Navegação
    const handleOpenOrganization = (organizationId) => {
        setShowResults(false);
        history.push(`/dashboard/config/organizations/edit/${organizationId}`);
    };

    const handleDeleteMany = async (ids) => {
        if (!appId || ids.length === 0) return;
        if (!window.confirm(`Tem certeza que deseja excluir ${ids.length} organização(ões)? Essa ação não pode ser desfeita.`)) return;

        const results = await Promise.allSettled(ids.map((id) => api.delete(`v1/apps/${appId}/organizations/${id}/`)));
        const failedCount = results.filter((r) => r.status === 'rejected').length;
        const succeededCount = results.length - failedCount;

        setStatus(
            failedCount > 0
                ? { type: 'warning', message: `${succeededCount} organização(ões) excluída(s), ${failedCount} falharam.` }
                : { type: 'success', message: `${succeededCount} organização(ões) excluída(s).` }
        );

        setSelectedOrganizations([]);
        listarOrganizacoes();
    };

    // 🔹 Importação de organizações via planilha
    const openImportDialog = () => {
        setImportResult(null);
        setImportParseError('');
        setImportDialogOpen(true);
    };

    const closeImportDialog = () => {
        if (importing) return;
        setImportDialogOpen(false);
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // permite selecionar o mesmo arquivo de novo depois
        if (!file || !appId) return;

        setImportResult(null);
        setImportParseError('');

        let rows;
        try {
            const buffer = await file.arrayBuffer();
            // cellDates precisa ser passado aqui (na leitura do workbook), não
            // no sheet_to_json - é o que faz uma célula formatada como data no
            // Excel virar um Date de verdade em vez do número de série interno
            // (ex: 45366); sem isso um campo customizado tipo "Data" importava
            // um número sem sentido, mesmo preenchido certinho.
            const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
            rows = parseSpreadsheetRows(workbook, fieldsConfig);
        } catch (error) {
            console.error('Erro ao ler planilha', error);
            setImportParseError('Não foi possível ler o arquivo. Confirme que é uma planilha .xlsx, .xls ou .csv válida.');
            return;
        }

        if (rows.length === 0) {
            setImportParseError('A planilha está vazia ou as colunas não foram reconhecidas.');
            return;
        }

        if (rows.length > IMPORT_MAX_ROWS) {
            setImportParseError(
                `A planilha tem ${rows.length} linhas - o limite é de ${IMPORT_MAX_ROWS} organizações por importação. Divida em arquivos menores.`
            );
            return;
        }

        try {
            setImporting(true);
            const response = await api.post(`v1/apps/${appId}/organizations/import/`, { organizations: rows });
            setImportResult(response.data);
            listarOrganizacoes();
        } catch (error) {
            console.error('Erro ao importar organizações', error);
            setImportParseError(error.response?.data?.error || 'Erro ao importar a planilha.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <Box sx={{ position: 'relative' }}>
            {/* HEADER COM BOTÃO */}
            <Grid item xs={12}>
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    Organizações
                </Typography>

                <Box
                    sx={{
                        // position: 'absolute',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'right',
                        mb: 3,
                        ml: 80
                    }}
                >
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={openImportDialog}>
                            Importar organizações
                        </Button>

                        <Button
                            variant="contained"
                            onClick={() => history.push('/dashboard/config/organizations/create')}
                            sx={{
                                borderRadius: '12px',
                                px: 3,
                                py: 1,
                                fontWeight: 600,
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                                boxShadow: '0 8px 20px rgba(25,118,210,0.3)',
                                transition: '0.3s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 12px 25px rgba(25,118,210,0.4)'
                                }
                            }}
                        >
                            Criar organização
                        </Button>
                    </Box>
                </Box>
            </Grid>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            {/* Barra de busca */}
            <OutlinedInput
                className={classes.searchControl}
                id="input-search-header"
                value={searchTerm}
                onChange={handleChange}
                placeholder="Buscar por Nome, CNPJ ou CPF..."
                sx={{
                    borderRadius: '12px',
                    background: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
                startAdornment={
                    <InputAdornment position="start">
                        <IconSearch size="1.2rem" color="#999" />
                    </InputAdornment>
                }
            />

            {/* Resultados da busca */}
            {showResults && searchResults.length > 0 && (
                <Paper className={classes.popperResults}>
                    <List dense>
                        {searchResults.map((organization) => (
                            <ListItem
                                key={organization.id}
                                className={classes.listItemHover}
                                onClick={() => handleOpenOrganization(organization)}
                            >
                                <ListItemText
                                    primary={organization.name}
                                    secondary={`${organization.cnpj || organization.cpf || 'Sem documento'} — ${
                                        organization.address || 'Sem endereço'
                                    }`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Card
                sx={{
                    mt: 4,
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
                }}
            >
                <CardContent>
                    {/* AÇÕES EM MASSA */}
                    {selectedOrganizations.length > 0 && (
                        <Box
                            sx={{
                                p: 2,
                                mb: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: 'linear-gradient(90deg, #f5f5f5, #fafafa)',
                                borderRadius: 2
                            }}
                        >
                            <Typography>{selectedOrganizations.length} selecionados</Typography>

                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteMany(selectedOrganizations)}
                                sx={{ borderRadius: '10px' }}
                            >
                                Excluir
                            </Button>
                        </Box>
                    )}

                    {/* HEADER */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 1fr 1fr 1.5fr 1fr 40px',
                            fontWeight: 'bold',
                            pb: 1,
                            borderBottom: '2px solid #eee'
                        }}
                    >
                        <Checkbox
                            checked={selectedOrganizations.length === organizations.length && organizations.length > 0}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedOrganizations(organizations.map((u) => u.id));
                                } else {
                                    setSelectedOrganizations([]);
                                }
                            }}
                        />

                        <Typography>Nome</Typography>
                        <Typography>CPF/CNPJ</Typography>
                        <Typography>Email</Typography>
                        <Typography>Endereço</Typography>
                        <Typography></Typography>
                    </Box>

                    {/* LINHAS */}
                    {organizations.map((organization) => (
                        <Box
                            key={organization.id}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '50px 1fr 1fr 1.5fr 1fr 40px',
                                borderBottom: '1px solid #f0f0f0',
                                py: 1.5,
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: '0.2s',
                                '&:hover': {
                                    background: '#f9fbff',
                                    transform: 'scale(1.002)'
                                }
                            }}
                            onClick={(e) => {
                                if (e.target.tagName !== 'INPUT' && !e.target.dataset.org) {
                                    handleOpenOrganization(organization.id);
                                }
                            }}
                        >
                            <Checkbox
                                checked={selectedOrganizations.includes(organization.id)}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    if (e.target.checked) {
                                        setSelectedOrganizations([...selectedOrganizations, organization.id]);
                                    } else {
                                        setSelectedOrganizations(selectedOrganizations.filter((id) => id !== organization.id));
                                    }
                                }}
                            />

                            <Typography fontWeight={500}>{organization.name}</Typography>

                            <Typography>{organization.cnpj || '—'}</Typography>

                            <Typography>{organization.email || '—'}</Typography>

                            <Typography>{organization.address || '—'}</Typography>

                            <Box>
                                <img
                                    src={'/company.png'}
                                    alt="organization-logo"
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            {/* IMPORTAR ORGANIZAÇÕES */}
            <Dialog open={importDialogOpen} onClose={closeImportDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Importar organizações</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} mb={1}>
                            Como montar a planilha:
                        </Typography>
                        <Typography variant="body2" component="div">
                            A primeira linha deve conter os nomes das colunas. Colunas aceitas (não diferencia
                            maiúscula/minúscula nem acento):
                        </Typography>
                        <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                            <li>
                                <Typography variant="body2">
                                    <b>Nome</b> — obrigatório
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">CNPJ — opcional; se já existir uma organização com o mesmo CNPJ, a linha é ignorada</Typography>
                            </li>
                            <li>
                                <Typography variant="body2">Email — opcional</Typography>
                            </li>
                            <li>
                                <Typography variant="body2">Endereço — opcional</Typography>
                            </li>
                            <li>
                                <Typography variant="body2">Descrição — opcional</Typography>
                            </li>
                            {fieldsConfig.map((f) => (
                                <li key={f.id}>
                                    <Typography variant="body2">
                                        <b>{f.title}</b> (campo customizado) — {f.required ? 'obrigatório' : 'opcional'}
                                        {f.type_field === 'boolean' ? ' — aceita "Sim"/"Não" ou "true"/"false"' : ''}
                                        {f.type_field === 'date' ? ' — formato AAAA-MM-DD (ex: 2024-03-15) ou célula formatada como data' : ''}
                                        {f.type_field === 'time' ? ' — formato HH:MM (ex: 14:30) ou célula formatada como hora' : ''}
                                        {f.type_field === 'select' ? ` — valores aceitos: ${(f.options || []).join(', ')}` : ''}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                        <Typography variant="body2">
                            Formatos aceitos: <b>.xlsx</b>, <b>.xls</b> ou <b>.csv</b>. Limite de <b>{IMPORT_MAX_ROWS} organizações</b>{' '}
                            por importação — planilhas maiores devem ser divididas em arquivos menores.
                        </Typography>
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<CloudDownloadIcon />}
                        fullWidth
                        sx={{ mb: 2 }}
                        onClick={() => downloadOrganizationTemplate(fieldsConfig)}
                    >
                        Baixar arquivo Excel de modelo
                    </Button>

                    {importParseError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {importParseError}
                        </Alert>
                    )}

                    {importResult && (
                        <Alert severity={importResult.error_count > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {importResult.created_count} organização(ões) importada(s) com sucesso.
                            </Typography>
                            {importResult.error_count > 0 && (
                                <>
                                    <Typography variant="body2" mt={1}>
                                        {importResult.error_count} linha(s) não importada(s):
                                    </Typography>
                                    <List dense>
                                        {importResult.errors.map((err) => (
                                            <ListItem key={err.row} disableGutters>
                                                <ListItemText
                                                    primary={`Linha ${err.row}${err.name ? ` (${err.name})` : ''}: ${err.error}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Alert>
                    )}

                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={importing ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={importing}
                        fullWidth
                    >
                        {importing ? 'Importando...' : 'Escolher planilha'}
                        <VisuallyHiddenInput type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} />
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeImportDialog} disabled={importing}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Debounce utilitário para evitar spam na API
function debounce(func, delay) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
}

export default ViewOrganization;
